"use client";

import { motion } from "framer-motion";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Phone,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Store,
  Truck,
  UserRound,
  Zap,
} from "lucide-react";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";

import { GoogleSignInButton } from "@/components/auth/google-sign-in-button";
import { Button } from "@/components/ui/button";
import { MovaLogo } from "@/components/ui/mova-logo";
import { useCartStore } from "@/components/providers/cart-store";
import { useWishlistStore } from "@/components/providers/wishlist-store";

type VendorType = { id: string; name: string; slug: string };

const DEFAULT_VENDOR_TYPES = [
  "Retail Store / Shop",
  "Electronics & Computers",
  "Smartphones & Gadgets",
  "Fashion, Clothing & Shoes",
  "Wholesaler / Bulk Supplier",
  "Distributor / Importer",
  "Brand Official Store",
  "General Merchandise",
];

export function AuthCard({ mode }: { mode: "login" | "signup" }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") ?? "/";
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Account type for registration: customer vs commercial vendor
  const initialType =
    searchParams.get("type") === "vendor" || searchParams.get("role") === "vendor"
      ? "vendor"
      : "customer";
  const [accountType, setAccountType] = useState<"customer" | "vendor">(initialType);
  const [vendorTypes, setVendorTypes] = useState<VendorType[]>([]);

  // Secure account linking state
  const [linkingState, setLinkingState] = useState<{
    active: boolean;
    credential: string;
    email: string;
  } | null>(null);
  const [linkPassword, setLinkPassword] = useState("");
  const [showLinkPassword, setShowLinkPassword] = useState(false);

  useEffect(() => {
    if (mode === "signup") {
      void fetch("/api/store/vendor-types")
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.vendorTypes && Array.isArray(data.vendorTypes)) {
            setVendorTypes(data.vendorTypes);
          }
        })
        .catch(() => {});
    }
  }, [mode]);

  async function handleGoogleCredential(credential: string, passwordToLink?: string) {
    setGoogleLoading(true);
    const isVendor = mode === "signup" && accountType === "vendor";
    try {
      if (mode === "signup") {
        try {
          if (typeof window !== "undefined") {
            window.sessionStorage?.setItem("mova_new_registration", "true");
            window.localStorage?.removeItem("mova-ethiopia-cart");
            window.localStorage?.removeItem("mova-ethiopia-wishlist");
          }
          useCartStore.getState().clearCart();
          useWishlistStore.setState({ items: [], wishlistOwnerId: null });
        } catch {
          // ignore
        }
      }

      const result = await signIn("google", {
        idToken: credential,
        linkPassword: passwordToLink,
        callbackUrl,
        accountType: isVendor ? "VENDOR" : "CUSTOMER",
      });

      if (result.requiresLinking) {
        setLinkingState({
          active: true,
          credential,
          email: result.email || "",
        });
        toast.info(result.error || "Please enter your password to link your Google account.");
        return;
      }

      if (result?.error) {
        throw new Error(result.error);
      }

      setLinkingState(null);
      toast.success(
        mode === "signup"
          ? isVendor
            ? "Commercial vendor account created with Google"
            : "Account created with Google"
          : "Welcome back",
      );
      const targetUrl =
        result?.role === "ADMIN"
          ? callbackUrl.startsWith("/admin")
            ? callbackUrl
            : "/admin"
          : result?.role === "ADMIN1"
            ? callbackUrl.startsWith("/admin1")
              ? callbackUrl
              : "/admin1"
            : result?.role === "VENDOR"
              ? callbackUrl.startsWith("/vendor")
                ? callbackUrl
                : "/vendor"
              : callbackUrl;
      router.push(targetUrl);
      router.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Google authentication failed.");
    } finally {
      setGoogleLoading(false);
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email"));
    const password = String(formData.get("password"));
    const isVendor = mode === "signup" && accountType === "vendor";

    try {
      if (mode === "signup") {
        // Ensure new customer starts with a completely empty cart and wishlist
        try {
          if (typeof window !== "undefined") {
            window.sessionStorage?.setItem("mova_new_registration", "true");
            window.localStorage?.removeItem("mova-ethiopia-cart");
            window.localStorage?.removeItem("mova-ethiopia-wishlist");
          }
          useCartStore.getState().clearCart();
          useWishlistStore.setState({ items: [], wishlistOwnerId: null });
        } catch {
          // ignore
        }

        const payload = {
          name: String(formData.get("name")),
          email,
          password,
          accountType: isVendor ? "VENDOR" : "CUSTOMER",
          ...(isVendor
            ? {
                businessName: String(formData.get("businessName") || ""),
                phone: String(formData.get("phone") || ""),
                vendorTypeId: String(formData.get("vendorTypeId") || "") || undefined,
                address: String(formData.get("address") || "") || undefined,
                taxId: String(formData.get("taxId") || "") || undefined,
                businessLicense: String(formData.get("businessLicense") || formData.get("taxId") || "") || undefined,
                description: String(formData.get("description") || "") || undefined,
              }
            : {}),
        };

        const response = await fetch("/api/auth/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          try {
            window.sessionStorage?.removeItem("mova_new_registration");
          } catch {
            // ignore
          }
          const data = await response.json();
          throw new Error(data.message ?? "Unable to create account.");
        }
      }

      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
        callbackUrl,
      });

      if (result?.error) {
        throw new Error(result.error);
      }

      toast.success(
        mode === "signup"
          ? isVendor
            ? "Commercial vendor account created! Welcome to MOVA Market."
            : "Account created"
          : "Welcome back",
      );
      const targetUrl =
        result?.role === "ADMIN"
          ? callbackUrl.startsWith("/admin")
            ? callbackUrl
            : "/admin"
          : result?.role === "ADMIN1"
            ? callbackUrl.startsWith("/admin1")
              ? callbackUrl
              : "/admin1"
            : result?.role === "VENDOR"
              ? callbackUrl.startsWith("/vendor")
                ? callbackUrl
                : "/vendor"
              : callbackUrl;
      router.push(targetUrl);
      router.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  const isVendorSignup = mode === "signup" && accountType === "vendor";

  // Login Mode (Standard compact centered card)
  if (mode === "login") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-5 sm:p-7 shadow-xs dark:border-white/10 dark:bg-slate-900"
      >
        <div className="mb-4 text-center">
          <div className="flex justify-center mb-2">
            <Link href="/" className="inline-flex items-center" aria-label="MOVA home">
              <MovaLogo size="md" showTagline />
            </Link>
          </div>
          <h1 className="mt-1 text-xl sm:text-2xl font-bold tracking-tight text-[#073B70] dark:text-white">
            {linkingState?.active ? "Link Google Account" : "Sign in"}
          </h1>
          <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
            {linkingState?.active
              ? "Verify your password to connect this Google profile to your existing MOVA account."
              : "Access orders, wishlist, cart sync, and seller tools."}
          </p>
        </div>

        {linkingState?.active ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleGoogleCredential(linkingState.credential, linkPassword);
            }}
          >
            <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200 flex gap-2.5">
              <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <p className="font-bold">Existing account found</p>
                <p className="mt-0.5">
                  An account with <strong>{linkingState.email}</strong> already exists with a password. Enter your password to securely link your Google account.
                </p>
              </div>
            </div>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-200">
                Account Password
              </span>
              <span className="flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 focus-within:border-[#087DB5] focus-within:ring-2 focus-within:ring-[#087DB5]/20 dark:border-white/10">
                <Lock className="h-4 w-4 shrink-0 text-slate-400" />
                <input
                  name="linkPassword"
                  type={showLinkPassword ? "text" : "password"}
                  value={linkPassword}
                  onChange={(e) => setLinkPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="Enter your existing account password"
                  className="h-10 flex-1 min-w-0 bg-transparent outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowLinkPassword((v) => !v)}
                  aria-label="Toggle password visibility"
                  className="p-1"
                >
                  {showLinkPassword ? (
                    <EyeOff className="h-4 w-4 text-slate-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-slate-400" />
                  )}
                </button>
              </span>
            </label>

            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-10 rounded-xl font-semibold text-xs"
                onClick={() => {
                  setLinkingState(null);
                  setLinkPassword("");
                }}
                disabled={googleLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1 h-10 rounded-xl font-bold text-xs"
                disabled={googleLoading || !linkPassword}
              >
                {googleLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Verify &amp; Link
              </Button>
            </div>
          </form>
        ) : (
          <>
            <div className="grid gap-2">
              <GoogleSignInButton
                mode="login"
                onCredential={(cred) => handleGoogleCredential(cred)}
                loading={googleLoading}
                disabled={loading}
                onError={(err) => toast.error(err)}
              />
            </div>

            <p className="mt-2 text-center text-sm">
              <Link href="/forgot-password" className="font-semibold text-[#087DB5] hover:text-[#073B70] hover:underline">
                Forgot password?
              </Link>
            </p>

            <div className="my-3 flex items-center gap-3 text-xs font-semibold uppercase text-slate-400">
              <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
              or
              <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
            </div>

            <form className="space-y-3" onSubmit={onSubmit}>
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-200">
                  Email
                </span>
                <span className="flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 focus-within:border-[#087DB5] focus-within:ring-2 focus-within:ring-[#087DB5]/20 dark:border-white/10">
                  <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                  <input
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    placeholder="email@example.com"
                    className="h-10 flex-1 min-w-0 bg-transparent outline-none text-sm"
                  />
                </span>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-200">
                  Password
                </span>
                <span className="flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 focus-within:border-[#087DB5] focus-within:ring-2 focus-within:ring-[#087DB5]/20 dark:border-white/10">
                  <Lock className="h-4 w-4 shrink-0 text-slate-400" />
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    minLength={6}
                    required
                    autoComplete="current-password"
                    placeholder="Minimum 6 characters"
                    className="h-10 flex-1 min-w-0 bg-transparent outline-none text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label="Toggle password visibility"
                    className="p-1"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                  </button>
                </span>
              </label>

              <Button
                type="submit"
                variant="primary"
                className="w-full h-11 rounded-xl text-sm font-bold shadow-sm"
                disabled={loading}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>

            <div className="mt-3.5 pt-2.5 border-t border-slate-100 dark:border-white/5 text-center">
              <Link
                href="/signup?type=vendor"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#087DB5] hover:text-[#073B70] hover:underline"
              >
                <Store className="h-3.5 w-3.5" />
                Want to sell on MOVA? Register as Commercial Vendor →
              </Link>
            </div>

            <p className="mt-3 text-center text-sm text-slate-600 dark:text-slate-300">
              New to MOVA?{" "}
              <Link
                href={`/signup?callbackUrl=${encodeURIComponent(callbackUrl)}`}
                className="font-bold text-[#087DB5] hover:text-[#073B70] transition-colors"
              >
                Create your account
              </Link>
            </p>
          </>
        )}
      </motion.div>
    );
  }

  // Registration Mode (Advanced Two-Panel Experience)
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="mx-auto w-full max-w-[1040px] rounded-3xl border border-slate-200/90 bg-white dark:border-white/10 dark:bg-slate-900 shadow-2xl shadow-slate-200/60 dark:shadow-black/70 overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr]">
        {/* ============================================================
            LEFT PANEL: Value Proposition & Brand Showcase
            ============================================================ */}
        <div className="hidden lg:flex flex-col justify-between p-8 text-white relative overflow-hidden bg-gradient-to-br from-[#062447] via-[#073B70] to-[#0A4F96]">
          {/* Ambient Glows & Patterns */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#087DB5]/35 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-sky-400/20 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.15)_1px,transparent_1px)] [background-size:20px_20px] opacity-20" />

          {/* Top Brand & Badge */}
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <Link href="/" className="inline-flex items-center" aria-label="MOVA home">
                <MovaLogo size="sm" />
              </Link>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-md px-3 py-1 text-[11px] font-bold text-sky-200 border border-white/15">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                {isVendorSignup ? "Commercial Portal" : "Customer Portal"}
              </span>
            </div>

            <div className="mt-8">
              <h2 className="text-2xl font-black tracking-tight text-white leading-snug">
                {isVendorSignup
                  ? "Launch your digital store across Ethiopia"
                  : "Premier shopping with full buyer protection"}
              </h2>
              <p className="mt-2 text-xs text-sky-100/85 leading-relaxed">
                {isVendorSignup
                  ? "Connect with thousands of retail and wholesale buyers across Addis Ababa and all regional cities."
                  : "Enjoy instant checkout, reliable doorstep delivery, guaranteed genuine quality, and exclusive member savings."}
              </p>
            </div>
          </div>

          {/* Feature Highlights */}
          <div className="relative z-10 my-6 space-y-3">
            {isVendorSignup ? (
              <>
                <div className="flex items-start gap-3 rounded-2xl bg-white/5 p-3 backdrop-blur-xs border border-white/10 transition-colors hover:bg-white/10">
                  <div className="rounded-xl bg-[#087DB5]/30 p-2 text-sky-200 shrink-0">
                    <Store className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-white">Custom Merchant Storefront</h3>
                    <p className="text-[11px] text-sky-200/75 leading-tight mt-0.5">
                      Your own branded store URL, custom catalog showcase, and direct inquiry management.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-2xl bg-white/5 p-3 backdrop-blur-xs border border-white/10 transition-colors hover:bg-white/10">
                  <div className="rounded-xl bg-emerald-500/25 p-2 text-emerald-300 shrink-0">
                    <Zap className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-white">Telebirr &amp; Bank Settlement</h3>
                    <p className="text-[11px] text-sky-200/75 leading-tight mt-0.5">
                      Guaranteed, automated payouts directly to your Telebirr merchant wallet or Ethiopian banks.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-2xl bg-white/5 p-3 backdrop-blur-xs border border-white/10 transition-colors hover:bg-white/10">
                  <div className="rounded-xl bg-amber-500/25 p-2 text-amber-300 shrink-0">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-white">Free Setup &bull; Zero Fees</h3>
                    <p className="text-[11px] text-sky-200/75 leading-tight mt-0.5">
                      No upfront charges or monthly fees. Start uploading and selling your products today.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start gap-3 rounded-2xl bg-white/5 p-3 backdrop-blur-xs border border-white/10 transition-colors hover:bg-white/10">
                  <div className="rounded-xl bg-[#087DB5]/30 p-2 text-sky-200 shrink-0">
                    <Truck className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-white">Fast Nationwide Delivery</h3>
                    <p className="text-[11px] text-sky-200/75 leading-tight mt-0.5">
                      Fast, tracked delivery in Addis Ababa and safe transport to all major Ethiopian hubs.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-2xl bg-white/5 p-3 backdrop-blur-xs border border-white/10 transition-colors hover:bg-white/10">
                  <div className="rounded-xl bg-emerald-500/25 p-2 text-emerald-300 shrink-0">
                    <ShieldCheck className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-white">100% Genuine Guarantee</h3>
                    <p className="text-[11px] text-sky-200/75 leading-tight mt-0.5">
                      Verified vendor products, tamper-free packaging, and effortless buyer returns.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-2xl bg-white/5 p-3 backdrop-blur-xs border border-white/10 transition-colors hover:bg-white/10">
                  <div className="rounded-xl bg-amber-500/25 p-2 text-amber-300 shrink-0">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-white">Exclusive Member Savings</h3>
                    <p className="text-[11px] text-sky-200/75 leading-tight mt-0.5">
                      Early access to deals, coupons, flash sales, and tailored recommendations.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ============================================================
            RIGHT PANEL: The Registration Form
            ============================================================ */}
        <div className="p-6 sm:p-8 flex flex-col justify-between bg-white dark:bg-slate-900">
          <div>
            {/* Header with Title & Mobile Logo */}
            <div className="flex items-center justify-between lg:justify-start gap-3 mb-1">
              <div className="lg:hidden">
                <Link href="/" className="inline-flex items-center" aria-label="MOVA home">
                  <MovaLogo size="sm" />
                </Link>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                  {linkingState?.active
                    ? "Link Google Account"
                    : isVendorSignup
                      ? "Commercial Store Registration"
                      : "Create Your Account"}
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {linkingState?.active
                    ? "Enter password to connect your Google profile."
                    : isVendorSignup
                      ? "Enter your store & contact details to open your merchant account."
                      : "Sign up to track orders, save addresses, and access member perks."}
                </p>
              </div>
            </div>

            {/* Segmented Account Type Switcher */}
            {!linkingState?.active && (
              <div className="grid grid-cols-2 p-1 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200/80 dark:border-white/10 my-3.5">
                <button
                  type="button"
                  onClick={() => setAccountType("customer")}
                  className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                    accountType === "customer"
                      ? "bg-white text-[#073B70] shadow-sm dark:bg-slate-700 dark:text-white"
                      : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                  }`}
                >
                  <UserRound className="h-3.5 w-3.5" />
                  Shopper / Customer
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType("vendor")}
                  className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                    accountType === "vendor"
                      ? "bg-[#087DB5] text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                  }`}
                >
                  <Store className="h-3.5 w-3.5" />
                  Commercial Vendor
                </button>
              </div>
            )}

            {linkingState?.active ? (
              <form
                className="space-y-4 max-w-md mx-auto pt-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleGoogleCredential(linkingState.credential, linkPassword);
                }}
              >
                <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200 flex gap-2.5">
                  <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-bold">Existing account found</p>
                    <p className="mt-0.5">
                      An account with <strong>{linkingState.email}</strong> already exists with a password. Enter your password to securely link your Google account.
                    </p>
                  </div>
                </div>

                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Account Password
                  </span>
                  <span className="flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 focus-within:border-[#087DB5] focus-within:ring-2 focus-within:ring-[#087DB5]/20 dark:border-white/10">
                    <Lock className="h-4 w-4 shrink-0 text-slate-400" />
                    <input
                      name="linkPassword"
                      type={showLinkPassword ? "text" : "password"}
                      value={linkPassword}
                      onChange={(e) => setLinkPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder="Enter your existing account password"
                      className="h-10 flex-1 min-w-0 bg-transparent outline-none text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLinkPassword((v) => !v)}
                      aria-label="Toggle password visibility"
                      className="p-1"
                    >
                      {showLinkPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-400" />
                      )}
                    </button>
                  </span>
                </label>

                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-10 rounded-xl font-semibold text-xs"
                    onClick={() => {
                      setLinkingState(null);
                      setLinkPassword("");
                    }}
                    disabled={googleLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="flex-1 h-10 rounded-xl font-bold text-xs"
                    disabled={googleLoading || !linkPassword}
                  >
                    {googleLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Verify &amp; Link
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={onSubmit}>
                {isVendorSignup ? (
                  /* COMMERCIAL VENDOR FORM: Clean 2-column layout */
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3.5 gap-y-2.5">
                      {/* Owner / Contact Name */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Owner / Contact Name *
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <UserRound className="h-4 w-4 shrink-0 text-slate-400" />
                          <input
                            name="name"
                            required
                            minLength={2}
                            autoComplete="name"
                            placeholder="e.g. Biruk Habte"
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                          />
                        </span>
                      </label>

                      {/* Business / Store Name */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Business / Store Name *
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <Store className="h-4 w-4 shrink-0 text-slate-400" />
                          <input
                            name="businessName"
                            required
                            minLength={2}
                            placeholder="e.g. Alsam Tech Store"
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                          />
                        </span>
                      </label>

                      {/* Email */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Email *
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                          <input
                            name="email"
                            type="email"
                            required
                            autoComplete="email"
                            inputMode="email"
                            placeholder="email@example.com"
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                          />
                        </span>
                      </label>

                      {/* Phone Number */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Phone Number *
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <Phone className="h-4 w-4 shrink-0 text-slate-400" />
                          <input
                            name="phone"
                            type="tel"
                            required
                            autoComplete="tel"
                            placeholder="+251 9... or 09..."
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                          />
                        </span>
                      </label>

                      {/* Store Category */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Store Category
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                          <select
                            name="vendorTypeId"
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm cursor-pointer text-slate-800 dark:text-slate-100"
                            defaultValue=""
                          >
                            <option value="" className="text-slate-500 dark:bg-slate-900">
                              Select Primary Category…
                            </option>
                            {vendorTypes.length > 0
                              ? vendorTypes.map((vt) => (
                                  <option key={vt.id} value={vt.id} className="dark:bg-slate-900">
                                    {vt.name}
                                  </option>
                                ))
                              : DEFAULT_VENDOR_TYPES.map((cat, idx) => (
                                  <option key={idx} value="" className="dark:bg-slate-900">
                                    {cat}
                                  </option>
                                ))}
                          </select>
                        </span>
                      </label>

                      {/* Password */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Password *
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <Lock className="h-4 w-4 shrink-0 text-slate-400" />
                          <input
                            name="password"
                            type={showPassword ? "text" : "password"}
                            minLength={6}
                            required
                            autoComplete="new-password"
                            placeholder="Minimum 6 characters"
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((value) => !value)}
                            aria-label="Toggle password visibility"
                            className="p-1 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                          </button>
                        </span>
                      </label>

                      {/* Store Address */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Store Address <span className="text-[11px] font-normal text-slate-400">(Optional)</span>
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
                          <input
                            name="address"
                            placeholder="e.g. Addis Ababa, Bole"
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                          />
                        </span>
                      </label>

                      {/* TIN / License # */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          TIN / License # <span className="text-[11px] font-normal text-slate-400">(Optional)</span>
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                          <input
                            name="taxId"
                            placeholder="e.g. 0012345678"
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                          />
                        </span>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full h-11 rounded-xl text-sm font-bold shadow-md hover:shadow-lg bg-gradient-to-r from-[#087DB5] to-[#073B70] hover:from-[#098ec7] hover:to-[#08488a] text-white flex items-center justify-center gap-2 mt-4 transition-all"
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Store className="h-4 w-4" />
                          <span>Register Commercial Store</span>
                          <ArrowRight className="h-4 w-4 ml-0.5" />
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  /* CUSTOMER REGISTRATION FORM */
                  <div className="space-y-3.5">
                    {/* Google 1-Click Button */}
                    <div>
                      <GoogleSignInButton
                        mode="signup"
                        onCredential={(cred) => handleGoogleCredential(cred)}
                        loading={googleLoading}
                        disabled={loading}
                        onError={(err) => toast.error(err)}
                      />
                    </div>

                    <div className="my-2 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
                      or create with email
                      <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Name */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Your Name *
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <UserRound className="h-4 w-4 shrink-0 text-slate-400" />
                          <input
                            name="name"
                            required
                            minLength={2}
                            autoComplete="name"
                            placeholder="e.g. Biruk Habte"
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                          />
                        </span>
                      </label>

                      {/* Email */}
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Email *
                        </span>
                        <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                          <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                          <input
                            name="email"
                            type="email"
                            required
                            autoComplete="email"
                            inputMode="email"
                            placeholder="email@example.com"
                            className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                          />
                        </span>
                      </label>
                    </div>

                    {/* Password */}
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-200">
                        Password *
                      </span>
                      <span className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 focus-within:border-[#087DB5] focus-within:bg-white focus-within:ring-3 focus-within:ring-[#087DB5]/15 dark:border-white/10 dark:bg-slate-800/40 dark:focus-within:bg-slate-800 transition-all">
                        <Lock className="h-4 w-4 shrink-0 text-slate-400" />
                        <input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          minLength={6}
                          required
                          autoComplete="new-password"
                          placeholder="Minimum 6 characters"
                          className="h-10 flex-1 min-w-0 bg-transparent outline-none text-xs sm:text-sm text-slate-900 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((value) => !value)}
                          aria-label="Toggle password visibility"
                          className="p-1 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                        </button>
                      </span>
                    </label>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full h-11 rounded-xl text-sm font-bold shadow-md hover:shadow-lg bg-gradient-to-r from-[#087DB5] to-[#073B70] hover:from-[#098ec7] hover:to-[#08488a] text-white flex items-center justify-center gap-2 mt-4 transition-all"
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <span>Create Customer Account</span>
                          <ArrowRight className="h-4 w-4 ml-0.5" />
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Bottom Context Switcher & Sign In Link */}
          {!linkingState?.active && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 text-xs text-slate-500 dark:text-slate-400 pt-3.5 mt-3.5 border-t border-slate-100 dark:border-white/10">
              {isVendorSignup ? (
                <button
                  type="button"
                  onClick={() => setAccountType("customer")}
                  className="inline-flex items-center gap-1 font-semibold text-[#087DB5] hover:text-[#073B70] hover:underline transition-colors"
                >
                  ← Looking to buy? Switch to Customer
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setAccountType("vendor")}
                  className="inline-flex items-center gap-1.5 font-semibold text-[#087DB5] hover:text-[#073B70] hover:underline transition-colors"
                >
                  <Store className="h-3.5 w-3.5" />
                  Commercial retailer or shop? Register as Vendor →
                </button>
              )}

              <p>
                Already have an account?{" "}
                <Link
                  href={`/login?callbackUrl=${encodeURIComponent(callbackUrl)}`}
                  className="font-bold text-[#087DB5] hover:text-[#073B70] hover:underline transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

